import { meta, rows } from '@/db/database'
import { currentCashier, requireDevice } from '@/lib/device'
import { newTempId } from '@/lib/tempId'
import { syncEngine } from '@/sync/SyncEngine'
import { PosModel, PosSync } from '@/types'

type Row = PosSync.LocalRow

const round = (n: number) => Math.round(n * 100) / 100

/** This terminal's open shift, if any. */
export const getOpenShift = async (): Promise<Row | undefined> => {
  const device = requireDevice()
  const shifts = await rows('terminal_shifts').where('terminal_id').equals(device.terminal_id).toArray()
  return shifts
    .filter((s) => !s.closed_at)
    .sort((a, b) => String(b.opened_at).localeCompare(String(a.opened_at)))[0]
}

/**
 * `YYYY-MMDD-T<unique_no>-S<counter>`, the counter per terminal and day like sale numbers; a wiped device resumes after
 * the highest number among the shifts the pull brought back.
 */
export const nextShiftNo = async (uniqueNo: number, now: Date) => {
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
  const prefix = `${day}-T${uniqueNo}-S`
  const counterKey = `shift_counter.${day}`
  const used = (await rows('terminal_shifts').toArray())
    .map((s) => String(s.shift_no ?? ''))
    .filter((n) => n.startsWith(prefix))
    .reduce((max, n) => Math.max(max, Number(n.slice(prefix.length)) || 0), 0)
  const counter = Math.max((await meta.get<number>(counterKey)) ?? 0, used) + 1
  await meta.set(counterKey, counter)
  return `${prefix}${counter}`
}

export const openShift = async (openingBalance: number) => {
  const device = requireDevice()
  if (await getOpenShift()) throw new Error('A shift is already open on this terminal.')
  const tempId = newTempId(device.terminal_unique_no)
  const now = new Date().toISOString()
  const shiftNo = await nextShiftNo(device.terminal_unique_no, new Date(now))
  await rows('terminal_shifts').add({
    id: tempId,
    temp_id: tempId,
    synced: 0,
    terminal_id: device.terminal_id,
    staff_id: currentCashier()?._id ?? device.staff_id,
    shift_no: shiftNo,
    opening_balance: openingBalance,
    total_cash_in: 0,
    total_cash_out: 0,
    opened_at: now,
    closed_at: null,
    created_at: now,
  })
  syncEngine.requestSync()
}

/** Cash put into or taken out of the drawer during the shift. The shift's running totals change with it. */
export const recordCash = async (type: 'cash_in' | 'cash_out', amount: number, remark?: string) => {
  const device = requireDevice()
  const shift = await getOpenShift()
  if (!shift) throw new Error('Open a shift first.')
  const tempId = newTempId(device.terminal_unique_no)
  await rows('terminal_cash_in_outs').add({
    id: tempId,
    temp_id: tempId,
    synced: 0,
    terminal_id: device.terminal_id,
    ...(shift._id ? { shift_id: shift._id } : { shift_temp_id: shift.temp_id }),
    staff_id: currentCashier()?._id ?? device.staff_id,
    type,
    amount,
    remark: remark || null,
    created_at: new Date().toISOString(),
  })
  const field = type === 'cash_in' ? 'total_cash_in' : 'total_cash_out'
  // The shift is pushed again with its new totals (shifts are mutable on the server, §15.9 rule 11).
  await rows('terminal_shifts').update(shift.id, { [field]: round(Number(shift[field] ?? 0) + amount), synced: 0 })
  syncEngine.requestSync()
}

export interface ShiftSummary {
  shift: Row
  sales: Record<PosModel.PaymentMethod, number>
  refunds: number
  cashRefunds: number
  repayments: number
  cashRepayments: number
  cashIn: number
  cashOut: number
  orderCount: number
  /** opening + cash sales − cash refunds + cash repayments + cash in − cash out (§6.2). */
  expectedCash: number
  /** What happened in the shift, newest first. */
  activity: ShiftEvent[]
}

export interface ShiftEvent {
  at: string
  kind: 'opened' | 'sale' | 'cancelled' | 'return' | 'cash_in' | 'cash_out' | 'repayment' | 'closed'
  text: string
  amount?: number
}

/**
 * The X report (open shift) or Z report (closed one), from this device's records in the shift's time window. The
 * server rebuilds the same figures when records arrive late (rule 18).
 */
export const summarise = async (shift: Row): Promise<ShiftSummary> => {
  const device = requireDevice()
  const from = String(shift.opened_at)
  const to = shift.closed_at ? String(shift.closed_at) : new Date().toISOString()
  const inShift = (row: Row) => row.terminal_id === device.terminal_id && String(row.created_at) >= from && String(row.created_at) <= to

  const [payments, repayments, cash, orders, returns] = await Promise.all([
    rows('payment_logs').toArray(),
    rows('credit_repayments').toArray(),
    rows('terminal_cash_in_outs').toArray(),
    rows('orders').toArray(),
    rows('order_returns').toArray(),
  ])
  const sales = { cash: 0, card: 0, bank: 0, qr: 0, credit: 0 } as Record<PosModel.PaymentMethod, number>
  let refunds = 0
  let cashRefunds = 0
  for (const p of payments.filter(inShift)) {
    const method = p.payment_method as PosModel.PaymentMethod
    if (p.type === 'payment' && method in sales) sales[method] += Number(p.amount_received) - Number(p.amount_returned ?? 0)
    if (p.type === 'refund') {
      refunds += Number(p.amount_returned)
      if (method === 'cash') cashRefunds += Number(p.amount_returned)
    }
  }
  // A repayment made here is a `credit_repayments` row; once synced it also comes back from the pull as a payment log.
  const localRepaymentIds = new Set(repayments.map((r) => r.temp_id))
  const shiftRepayments = [
    ...repayments,
    ...payments
      .filter((p) => p.type === 'repayment' && !localRepaymentIds.has(p.temp_id))
      .map((p): Row => ({ ...p, amount: p.amount_received })),
  ].filter(inShift)
  const repaid = shiftRepayments.reduce((s, r) => s + Number(r.amount), 0)
  const cashRepaid = shiftRepayments.filter((r) => (r.payment_method ?? 'cash') === 'cash').reduce((s, r) => s + Number(r.amount), 0)
  const shiftCash = cash.filter((c) => c.shift_id === shift._id || c.shift_temp_id === shift.temp_id)
  const cashIn = shiftCash.filter((c) => c.type === 'cash_in').reduce((s, c) => s + Number(c.amount), 0)
  const cashOut = shiftCash.filter((c) => c.type === 'cash_out').reduce((s, c) => s + Number(c.amount), 0)

  Object.keys(sales).forEach((k) => (sales[k as PosModel.PaymentMethod] = round(sales[k as PosModel.PaymentMethod])))

  const shiftOrders = orders.filter(inShift)
  const terminalOrders = new Set<unknown>(orders.filter((o) => o.terminal_id === device.terminal_id).flatMap((o) => [o._id, o.temp_id].filter(Boolean)))
  const inWindow = (row: Row) => String(row.created_at) >= from && String(row.created_at) <= to
  const activity: ShiftEvent[] = [
    { at: from, kind: 'opened' as const, text: 'Shift opened', amount: Number(shift.opening_balance ?? 0) },
    ...shiftOrders.map((o): ShiftEvent => ({
      at: String(o.created_at),
      kind: o.status === 'cancelled' ? 'cancelled' : 'sale',
      text: `${o.status === 'cancelled' ? 'Cancelled' : 'Sale'} ${o.order_ref}`,
      amount: Number(o.total),
    })),
    ...returns
      .filter((r) => inWindow(r) && (terminalOrders.has(r.order_id) || terminalOrders.has(r.order_temp_id)))
      .map((r): ShiftEvent => ({ at: String(r.created_at), kind: 'return', text: `Return ${r.return_ref}`, amount: Number(r.total_refund_amount) })),
    ...shiftCash.map((c): ShiftEvent => ({
      at: String(c.created_at),
      kind: c.type === 'cash_in' ? 'cash_in' : 'cash_out',
      text: `${c.type === 'cash_in' ? 'Cash in' : 'Cash out'}${c.remark ? ` · ${c.remark}` : ''}`,
      amount: Number(c.amount),
    })),
    ...shiftRepayments.map((r): ShiftEvent => ({ at: String(r.created_at), kind: 'repayment', text: 'Credit repaid', amount: Number(r.amount) })),
    ...(shift.closed_at ? [{ at: String(shift.closed_at), kind: 'closed' as const, text: 'Shift closed' }] : []),
  ].sort((a, b) => b.at.localeCompare(a.at))

  return {
    shift,
    sales,
    refunds: round(refunds),
    cashRefunds: round(cashRefunds),
    repayments: round(repaid),
    cashRepayments: round(cashRepaid),
    cashIn: round(cashIn),
    cashOut: round(cashOut),
    orderCount: orders.filter(inShift).length,
    expectedCash: round(Number(shift.opening_balance ?? 0) + sales.cash - cashRefunds + cashRepaid + cashIn - cashOut),
    activity,
  }
}

export const closeShift = async (closingBalance: number): Promise<ShiftSummary> => {
  const shift = await getOpenShift()
  if (!shift) throw new Error('No open shift.')
  const closedAt = new Date().toISOString()
  const summary = await summarise({ ...shift, closed_at: closedAt })
  await rows('terminal_shifts').update(shift.id, {
    closed_at: closedAt,
    closing_balance: closingBalance,
    expected_balance: summary.expectedCash,
    discrepancy: round(closingBalance - summary.expectedCash),
    synced: 0,
  })
  syncEngine.requestSync()
  return { ...summary, shift: { ...shift, closed_at: closedAt, closing_balance: closingBalance } }
}

/** The register the till shows: this terminal, the signed-in cashier, the open shift. */
export const getRegister = async (): Promise<PosModel.Register> => {
  const device = requireDevice()
  const [terminal, shift, branch] = await Promise.all([
    rows('terminals').where('_id').equals(device.terminal_id).first(),
    getOpenShift(),
    rows('branches').where('_id').equals(device.branch_id).first(),
  ])
  return {
    _id: device.terminal_id,
    name: String(terminal?.name ?? `Terminal ${device.terminal_unique_no}`),
    terminal_no: device.terminal_unique_no,
    branch: branch?.name ? String(branch.name) : undefined,
    cashier: currentCashier()?.name ?? '',
    opened_at: shift ? String(shift.opened_at) : '',
    shift_no: shift?.shift_no ? String(shift.shift_no) : undefined,
    opening_float: Number(shift?.opening_balance ?? 0),
    // Tax comes from each product's own VAT on a real device; this rate is only the demo's.
    tax_rate: 0,
    currency: 'BDT',
  }
}
